import { useCallback, useEffect, useState } from 'react';
import { useTheme, useTokens } from '../theme.jsx';
import {
  getInstitutionInvoices,
  getSuperAdminInstitutionBilling,
  refundInstitutionInvoice,
} from '../services/institutions.js';
import { notifyError, notifySuccess } from '../toast.js';
import {
  Button, Card, ErrorState, Field, SectionTitle, StatusBadge,
} from '../uiKit.jsx';
import { RowsSkeleton } from '../Skeleton.jsx';
import { VIEW_TO_PATH } from '../../portal/portalUrl.js';

const currency = (cents, code = 'USD') => {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: code || 'USD',
  }).format((Number(cents) || 0) / 100);
};

const date = (value) => {
  if (!value) { return '—'; }
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(value));
};

const STATUS_LABELS = {
  paid: 'Paid',
  open: 'Open',
  draft: 'Draft',
  void: 'Void',
  uncollectible: 'Uncollectible',
};

const requestKey = () => {
  if (globalThis.crypto?.randomUUID) { return globalThis.crypto.randomUUID(); }
  return `refund_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

const detailValue = (value) => {
  return value === null || value === undefined || value === '' ? '—' : String(value);
};

export default function InvoicesPanel({ institutionId, superAdmin = false }) {
  const t = useTokens();
  const portal = useTheme() === 'portal';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refundDraft, setRefundDraft] = useState(null);
  const [refundBusy, setRefundBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setData(superAdmin
        ? await getSuperAdminInstitutionBilling(institutionId)
        : await getInstitutionInvoices(institutionId));
    } catch {
      setData(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [institutionId, superAdmin]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <Card><SectionTitle>Invoices</SectionTitle><RowsSkeleton rows={4} /></Card>;
  }

  if (error) {
    return (
      <Card>
        <SectionTitle>Invoices</SectionTitle>
        <ErrorState message="Invoices could not be loaded. Try again in a moment." onRetry={load} retryLabel="Try again" />
      </Card>
    );
  }

  const invoices = [...(data?.invoices || [])]
    .sort((a, b) => { return new Date(b.createdAt || 0) - new Date(a.createdAt || 0); });
  const unavailable = !data?.available;
  const noBillingCustomer = data?.reason === 'no_billing_customer';
  // invoice_read_failed (a Stripe customer exists but the read failed) and any
  // unmapped reason are an outage: say so and offer the same retry.
  const readFailed = unavailable && !noBillingCustomer && data?.reason !== 'stripe_not_configured';
  const billing = data?.billing || {};
  const overage = billing.overagePool || {};
  const overrides = billing.entitlementOverrides || {};

  const openRefund = (invoice) => {
    setRefundDraft({
      invoice,
      amount: ((Number(invoice.refundableAmountCents) || Number(invoice.amountPaidCents) || 0) / 100).toFixed(2),
      reason: 'requested_by_customer',
      requestId: requestKey(),
    });
  };

  const submitRefund = async () => {
    const amountCents = Math.round(Number(refundDraft?.amount) * 100);
    if (!Number.isInteger(amountCents) || amountCents <= 0) {
      notifyError('Enter a valid refund amount.');
      return;
    }
    if (amountCents > Number(refundDraft.invoice.refundableAmountCents || refundDraft.invoice.amountPaidCents || 0)) {
      notifyError('Refund amount exceeds the refundable balance.');
      return;
    }
    setRefundBusy(true);
    try {
      await refundInstitutionInvoice(institutionId, refundDraft.invoice.id, {
        amountCents,
        reason: refundDraft.reason,
        requestId: refundDraft.requestId,
      });
      notifySuccess(`${currency(amountCents, refundDraft.invoice.currency)} refund submitted.`);
      setRefundDraft(null);
      await load();
    } catch (refundError) {
      notifyError(refundError?.response?.data?.error || 'Could not refund this invoice.');
    } finally {
      setRefundBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <SectionTitle>Billing summary</SectionTitle>
        <div className={`grid gap-px overflow-hidden rounded-xl border ${superAdmin ? 'sm:grid-cols-4' : 'sm:grid-cols-3'}`} style={{ borderColor: t.border, backgroundColor: t.hairline }}>
          {[
            ['Plan', data?.billing?.seatPlan || '—'],
            ['Billing status', data?.billing?.status || (noBillingCustomer ? 'Not connected' : (readFailed ? 'Unavailable' : 'Not set'))],
            ...(superAdmin ? [['Seats', `${billing.seatsUsed || 0} / ${billing.seatsPurchased || 0}`]] : []),
            [superAdmin ? 'Billing period' : 'Current period ends', superAdmin
              ? `${date(data?.billing?.currentPeriodStart)} – ${date(data?.billing?.currentPeriodEnd)}`
              : date(data?.billing?.currentPeriodEnd)],
          ].map(([label, value]) => {
            return (
              <div key={label} className="px-4 py-3" style={{ backgroundColor: t.statBg }}>
                <div className={`${portal ? 'text-[13px]' : 'text-xs'} uppercase tracking-wide`} style={{ color: t.muted, fontFamily: t.fonts.mono }}>{label}</div>
                <div className="mt-1 text-base" style={{ color: t.heading }}>{value}</div>
              </div>
            );
          })}
        </div>
        {superAdmin ? (
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <div className="rounded-xl border p-4" style={{ borderColor: t.border }}>
              <h4 className="font-semibold" style={{ color: t.heading }}>Stripe account</h4>
              <dl className="mt-3 space-y-2 text-sm">
                {[
                  ['Customer', data?.customer?.id || billing.stripeCustomerId],
                  ['Customer name', data?.customer?.name],
                  ['Billing contact', data?.customer?.email],
                  ['Subscription', data?.subscription?.id || billing.stripeSubscriptionId],
                  ['Live subscription status', data?.subscription?.status],
                  ['Collection', data?.subscription?.collectionMethod],
                  ['Cancel at period end', data?.subscription?.cancelAtPeriodEnd ? 'Yes' : 'No'],
                  ['Delinquent', data?.customer?.delinquent ? 'Yes' : 'No'],
                  ['Customer balance', currency(data?.customer?.balanceCents || 0, data?.customer?.currency || 'USD')],
                ].map(([label, value]) => {
                  return (
                    <div key={label} className="flex justify-between gap-4">
                      <dt style={{ color: t.muted }}>{label}</dt>
                      <dd className="text-right break-all" style={{ color: t.heading, fontFamily: label === 'Customer' || label === 'Subscription' ? t.fonts.mono : t.fonts.body }}>{detailValue(value)}</dd>
                    </div>
                  );
                })}
              </dl>
            </div>
            <div className="rounded-xl border p-4" style={{ borderColor: t.border }}>
              <h4 className="font-semibold" style={{ color: t.heading }}>Institution funding</h4>
              <dl className="mt-3 space-y-2 text-sm">
                {[
                  ['Overage pool', overage.enabled ? 'On' : 'Off'],
                  ['Overage cap', currency(overage.capCents || 0)],
                  ['Overage spent', currency(overage.spentCents || 0)],
                  ...Object.entries(overrides)
                    .filter(([, value]) => { return typeof value === 'number'; })
                    .map(([key, value]) => { return [`Custom ${key}`, value]; }),
                ].map(([label, value]) => {
                  return (
                    <div key={label} className="flex justify-between gap-4">
                      <dt style={{ color: t.muted }}>{label}</dt>
                      <dd className="text-right" style={{ color: t.heading }}>{detailValue(value)}</dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          </div>
        ) : null}
      </Card>

      <Card>
        <SectionTitle>Invoices</SectionTitle>
        {readFailed ? (
          <ErrorState message="We can't load invoices right now." onRetry={load} retryLabel="Try again" />
        ) : null}
        {unavailable && noBillingCustomer ? (
          <div>
            <p className="text-sm" style={{ color: t.muted }}>
              {superAdmin
                ? 'Invoices will appear here after institution billing is connected.'
                : 'Invoices are sent to you directly while online billing is being set up.'}
            </p>
            {!superAdmin ? (
              <a
                href={`${VIEW_TO_PATH.support}?type=BILLING`}
                className={`mt-1 min-h-[44px] inline-flex items-center text-sm underline focus:outline-none focus-visible:ring-2 ${t.focusRing}`}
                style={{ color: t.heading }}
              >
                Request an invoice
              </a>
            ) : null}
          </div>
        ) : null}
        {unavailable && data?.reason === 'stripe_not_configured' ? (
          <p className="text-sm" style={{ color: t.muted }}>Invoice history is not available in this environment.</p>
        ) : null}
        {!unavailable && invoices.length === 0 ? (
          <p className="text-sm" style={{ color: t.muted }}>No invoices have been issued yet.</p>
        ) : null}
        {!unavailable && invoices.length > 0 ? (
          <div className="divide-y" style={{ borderColor: t.hairline }}>
            {invoices.map((invoice) => {
              const primaryUrl = invoice.hostedInvoiceUrl || invoice.invoicePdfUrl;
              return (
                <div key={invoice.id} className="py-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold" style={{ color: t.heading }}>{invoice.number || 'Invoice'}</span>
                      <StatusBadge
                        tone={invoice.status === 'paid' ? 'positive' : 'neutral'}
                        label={STATUS_LABELS[invoice.status] || invoice.status || 'Unknown'}
                      />
                    </div>
                    <p className="mt-1 text-sm" style={{ color: t.muted }}>
                      {date(invoice.createdAt)}{invoice.description ? ` · ${invoice.description}` : ''}
                    </p>
                    {superAdmin && invoice.paymentMethod?.last4 ? (
                      <p className="mt-1 text-xs" style={{ color: t.muted }}>
                        {invoice.paymentMethod.brand || 'Card'} •••• {invoice.paymentMethod.last4}
                        {invoice.amountRefundedCents ? ` · ${currency(invoice.amountRefundedCents, invoice.currency)} refunded` : ''}
                      </p>
                    ) : null}
                    {superAdmin ? (
                      <p className="mt-1 text-[11px] break-all" style={{ color: t.muted, fontFamily: t.fonts.mono }}>
                        {invoice.id}{invoice.paymentIntentId ? ` · ${invoice.paymentIntentId}` : ''}{invoice.chargeId ? ` · ${invoice.chargeId}` : ''}
                      </p>
                    ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 sm:justify-end">
                    <span className="text-base tabular-nums" style={{ color: t.heading, fontFamily: t.fonts.mono }}>
                      {currency(invoice.amountDueCents, invoice.currency)}
                    </span>
                    {primaryUrl ? (
                      <a href={primaryUrl} className={`min-h-[44px] inline-flex items-center text-sm underline focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={{ color: t.heading }}>
                        View invoice
                      </a>
                    ) : null}
                    {superAdmin && Number(invoice.refundableAmountCents) > 0 ? (
                      <Button small variant="secondary" label="Refund" onClick={() => { openRefund(invoice); }} />
                    ) : null}
                    </div>
                  </div>
                  {superAdmin && refundDraft?.invoice.id === invoice.id ? (
                    <div className="mt-4 rounded-xl border p-4" style={{ borderColor: t.border, backgroundColor: t.statBg }}>
                      <p className="text-sm font-semibold" style={{ color: t.heading }}>Refund {invoice.number || 'invoice'}</p>
                      <p className="mt-1 text-sm" style={{ color: t.muted }}>
                        The refund returns to the original payment method. Up to {currency(invoice.refundableAmountCents, invoice.currency)} is currently refundable.
                      </p>
                      <div className="mt-4 grid gap-3 sm:grid-cols-[180px_1fr_auto] sm:items-end">
                        <label className="text-xs" style={{ color: t.body }}>
                          <span className="mb-1 block">Amount ({invoice.currency?.toUpperCase()})</span>
                          <Field value={refundDraft.amount} onChangeText={(amount) => { setRefundDraft((current) => { return { ...current, amount }; }); }} inputMode="decimal" />
                        </label>
                        <label className="text-xs" style={{ color: t.body }}>
                          <span className="mb-1 block">Reason</span>
                          <select
                            value={refundDraft.reason}
                            onChange={(event) => { setRefundDraft((current) => { return { ...current, reason: event.target.value }; }); }}
                            className="h-[44px] w-full rounded-md border px-3 text-sm"
                            style={{ backgroundColor: t.inputBg, borderColor: t.inputBorder, color: t.heading }}
                          >
                            <option value="requested_by_customer">Requested by customer</option>
                            <option value="duplicate">Duplicate charge</option>
                            <option value="fraudulent">Fraudulent</option>
                          </select>
                        </label>
                        <div className="flex gap-2">
                          <Button small variant="secondary" label="Cancel" disabled={refundBusy} onClick={() => { setRefundDraft(null); }} />
                          <Button small label={refundBusy ? 'Refunding…' : 'Confirm refund'} disabled={refundBusy} onClick={submitRefund} />
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </Card>
    </div>
  );
}
